// Live Area Pages data from Page Maker, reached through the CRM-side proxy the token
// endpoint points at (the tenant api key stays on the server). On any failure the
// index shows an empty list rather than invented data.

const devLog = (...args) => {
  // eslint-disable-next-line no-console
  if (process.env.NODE_ENV === "development") console.log("[PageMaker]", ...args);
};

let cachedCreds = null;

// Per-user credentials, cached until shortly before the token's five-minute expiry.
const getCredentials = async () => {
  if (cachedCreds && Date.now() < cachedCreds.expiresAtMs - 30000) return cachedCreds;
  const resp = await fetch("/website/page_maker/token", { credentials: "same-origin" });
  if (!resp.ok) throw new Error(`token endpoint returned ${resp.status}`);
  const { token, api_url: apiBase, expires_at: expiresAt } = await resp.json();
  if (!token || !apiBase) throw new Error("token endpoint response incomplete");
  devLog("JWT:", token);
  devLog("claims:", JSON.parse(atob(token.split(".")[1])));
  cachedCreds = { token, apiBase, expiresAtMs: expiresAt * 1000 };
  return cachedCreds;
};

const pmGet = async (path) => {
  const { token, apiBase } = await getCredentials();
  const resp = await fetch(`${apiBase}${path}`, {
    credentials: "same-origin",
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!resp.ok) throw new Error(`GET ${path} returned ${resp.status}`);
  return (await resp.json()).data;
};

const pmPost = async (path, body) => {
  const { token, apiBase } = await getCredentials();
  const resp = await fetch(`${apiBase}${path}`, {
    method: "POST",
    credentials: "same-origin",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
  // Error responses carry a user-presentable message in the envelope (e.g. duplicate slug).
  const payload = await resp.json().catch(() => ({}));
  if (!resp.ok) throw new Error(payload.error || `POST ${path} returned ${resp.status}`);
  return payload.data;
};

// One flight shared between the list and the status counts on the same page load.
let pagesPromise = null;

const fetchAllPages = () => {
  if (pagesPromise) return pagesPromise;
  pagesPromise = (async () => {
    const { sites = [] } = await pmGet("/sites");
    devLog("sites for this user:", sites);
    const rowsBySite = await Promise.all(
      sites.map(async (site) => {
        const siteLabel = site.subdomain ? `${site.subdomain}.${site.domain}` : site.domain;
        // Area pages are the ones built from Area-category templates; the rest of the
        // site's pages stay out of this index.
        const { pages = [] } = await pmGet(`/sites/${site.uuid}/pages-with-settings?categoryName=Area`);
        devLog(`pages for ${siteLabel}:`, pages);
        return pages.map((p) => ({
          uuid: p.uuid,
          title: p.name,
          area: siteLabel,
          // The author-facing draft/published state lives in the page's settings.
          isPublished: p.settings?.status === "published",
          isArchived: false,
          datePublished: p.settings?.datePublished || null,
          dateCreated: p.dateCreated,
          createdBy: null,
          featuredImageUrl: null,
          path: p.slug,
        }));
      }),
    );
    return rowsBySite.flat();
  })().finally(() => {
    setTimeout(() => {
      pagesPromise = null;
    }, 2000);
  });
  return pagesPromise;
};

const statusOf = (p) => {
  if (p.isArchived) return "archived";
  if (p.isPublished) return "published";
  return "drafts";
};

const matchesStatus = (p, publishStatus) => publishStatus === "all" || statusOf(p) === publishStatus;

export const getAreaPages = async ({
  page = 1,
  itemsPerPage = 10,
  publishStatus = "all",
  orderBy = "dateCreated",
  sortOrder = "desc",
  searchTerm = "",
} = {}) => {
  let source = [];
  try {
    source = await fetchAllPages();
  } catch (e) {
    devLog("fetch failed, showing an empty list:", e.message);
  }

  const filtered = source
    .filter((p) => matchesStatus(p, publishStatus))
    .filter((p) => !searchTerm || p.title.toLowerCase().includes(searchTerm.toLowerCase()));

  const sorted = [...filtered].sort((a, b) => {
    const dir = sortOrder === "asc" ? 1 : -1;
    const av = orderBy === "status" ? statusOf(a) : a[orderBy] || "";
    const bv = orderBy === "status" ? statusOf(b) : b[orderBy] || "";
    if (av < bv) return -1 * dir;
    if (av > bv) return 1 * dir;
    return 0;
  });

  const totalPages = Math.max(1, Math.ceil(sorted.length / itemsPerPage));
  const start = (page - 1) * itemsPerPage;
  const pages = sorted.slice(start, start + itemsPerPage);

  return { pages, totalPages };
};

export const getAreaPageStatusCounts = async () => {
  let source = [];
  try {
    source = await fetchAllPages();
  } catch (e) {
    devLog("fetch failed, counts are zero:", e.message);
  }
  return {
    all: source.length,
    published: source.filter((p) => statusOf(p) === "published").length,
    drafts: source.filter((p) => statusOf(p) === "drafts").length,
    archived: source.filter((p) => statusOf(p) === "archived").length,
  };
};

export const isTokenValid = (token, expiresAt) => !!token && !!expiresAt && Date.now() < expiresAt - 30_000; // refresh 30s early

// The account's main site, as recorded by the sites sync. No flagged site is an error,
// never a fallback to an arbitrary one.
export const getMainSite = async () => {
  const { sites = [] } = await pmGet("/sites");
  const main = sites.find((site) => site.isMain);
  if (!main) throw new Error("This account has no main site set up, so the page can't be created.");
  return main;
};

// Area pages are created from a template of the Area category; that template link is
// what the filtered index recognizes them by.
const getAreaTemplate = async () => {
  const { pageTemplates = [] } = await pmGet("/page-templates?categoryName=Area");
  const template = pageTemplates.find((t) => t.isActive);
  if (!template) throw new Error("No active Area page template is available, so the page can't be created.");
  return template;
};

export const createAreaPage = async ({ title, slug }, { isCancelled = () => false } = {}) => {
  const [site, template] = await Promise.all([getMainSite(), getAreaTemplate()]);
  // A cancel confirmed during the lookup wins: nothing gets created.
  if (isCancelled()) return null;
  devLog("creating page on main site:", site.uuid, title, slug, "from template:", template.uuid);
  const page = await pmPost(`/sites/${site.uuid}/pages`, {
    originPageTemplateUuid: template.uuid,
    name: title,
    slug,
    path: `/${slug}`,
    isActive: true,
  });
  // The index must not reuse a snapshot taken before this page existed.
  pagesPromise = null;
  return page;
};
